import { Link } from "react-router-dom";
import { Pencil, ExternalLink, Trash2, Mail } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { useLang } from "../../lib/i18n";

const StatusBadge = ({ emp, id }) => {
  const { t } = useLang();
  const pending = emp.is_active && !emp.joined_at;
  const [label, cls] = !emp.is_active ? ["Inactive", "bg-slate-100 text-slate-500"]
    : pending ? ["Pending", "bg-amber-50 text-amber-700"] : ["Active", "bg-emerald-50 text-emerald-700"];
  return (
  <span data-testid={`employee-status-${id}`}
    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${cls}`}>
    {t(label)}
  </span>
  );
};

const InviteStatus = ({ emp }) => {
  const { t } = useLang();
  if (!emp.is_active || emp.joined_at) return null;
  return <span className="mt-1 block text-[11px] font-medium text-slate-400" data-testid={`employee-invite-status-${emp.id}`}>{t(emp.invited_at ? "Invite not accepted yet" : "Not invited")}</span>;
};

const Row = ({ emp, onEdit, onToggle, onDelete, onInvite, roleName, canEdit, canToggle, canDelete, canInvite, isSelf, selected, onSelect }) => {
  const { t, pick } = useLang();
  return (
  <tr className="border-t border-slate-100 transition-colors hover:bg-slate-50/70" data-testid={`employee-row-${emp.id}`}>
    <td className="w-10 ps-4">{!isSelf && <Checkbox checked={selected} onCheckedChange={() => onSelect(emp.id)} data-testid={`employee-select-${emp.id}`} aria-label={t("Select")} />}</td>
    <td className="px-4 py-2">
      <p className="font-semibold text-slate-900">{pick(emp.name_en, emp.name_ar)}</p>
      {emp.name_ar && <p className="text-start text-sm text-slate-500">{pick(emp.name_ar, emp.name_en)}</p>}
    </td>
    <td className="px-4 py-2 text-sm text-slate-700">{pick(emp.title_en, emp.title_ar)}</td>
    <td dir="ltr" className="px-4 py-2 text-end font-mono text-xs text-slate-600 rtl:text-right">{emp.email}</td>
    <td className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500" data-testid={`employee-role-${emp.id}`}>{roleName}</td>
    <td className="px-4 py-2"><StatusBadge emp={emp} id={emp.id} /><InviteStatus emp={emp} /></td>
    <td className="px-4 py-2">
      <div className="flex items-center justify-end gap-2">
        {canInvite && emp.is_active && !emp.joined_at && <button onClick={() => onInvite(emp)} data-testid={`invite-employee-${emp.id}`}
          title={t(emp.invited_at ? "Resend invite" : "Send invite")}
          className="flex h-10 w-10 items-center justify-center rounded-xl text-cobalt hover:bg-slate-100">
          <Mail className="h-4 w-4" />
        </button>}
        {canToggle && !isSelf && <Switch checked={emp.is_active} onCheckedChange={() => onToggle(emp)} data-testid={`deactivate-card-button-${emp.id}`} aria-label={t("Turn card on or off")} />}
        <Link to={`/c/${emp.slug}`} target="_blank" data-testid={`open-card-${emp.id}`} title={t("Open card")}
          className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-900">
          <ExternalLink className="h-4 w-4" />
        </Link>
        {canEdit && <button onClick={() => onEdit(emp)} data-testid={`edit-employee-${emp.id}`} title={t("Edit")}
          className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-900">
          <Pencil className="h-4 w-4" />
        </button>}
        {canDelete && !isSelf && <button onClick={() => onDelete(emp)} data-testid={`delete-employee-${emp.id}`} title={t("Delete permanently")}
          className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 hover:bg-red-50 hover:text-wade">
          <Trash2 className="h-4 w-4" />
        </button>}
      </div>
    </td>
  </tr>
  );
};

export const EmployeeTable = ({ employees, loading, onEdit, onToggle, onDelete, onInvite, roles, canEdit, canToggle, canDelete, canInvite, currentEmail, emptyText, selected, onSelect, onSelectAll }) => {
  const { t, pick } = useLang();
  const names = Object.fromEntries(roles.map((r) => [r.key, pick(r.name_en, r.name_ar)]));
  return (
  <div className="overflow-x-auto rounded-3xl border border-slate-200/80 bg-white" data-testid="admin-employee-table">
    <table className="w-full min-w-[760px] text-start">
      <thead>
        <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
          <th className="w-10 ps-4"><Checkbox checked={employees.length > 0 && employees.filter((e) => e.email !== currentEmail).every((e) => selected.has(e.id))}
            onCheckedChange={(v) => onSelectAll(v)} data-testid="employee-select-all" aria-label={t("Select all")} /></th>
          {["Name", "Title", "Email", "Role", "Status", ""].map((h) => <th key={h} className="px-5 py-3 text-start">{h && t(h)}</th>)}
        </tr>
      </thead>
      <tbody>
        {employees.map((emp) => (
          <Row key={emp.id} emp={emp} onEdit={onEdit} onToggle={onToggle} onDelete={onDelete} onInvite={onInvite} roleName={names[emp.role] || emp.role}
            canEdit={canEdit} canToggle={canToggle} canDelete={canDelete} canInvite={canInvite} isSelf={emp.email === currentEmail}
            selected={selected.has(emp.id)} onSelect={onSelect} />
        ))}
        {!loading && employees.length === 0 && (
          <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-slate-500" data-testid="employee-table-empty">{emptyText}</td></tr>
        )}
      </tbody>
    </table>
  </div>
  );
};
